import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Home,
  Gamepad2,
  Trophy,
  User,
  Crown,
  BarChart3,
  Settings,
  Server
} from 'lucide-react';

const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      current: location.pathname === '/dashboard'
    },
    {
      name: 'Games',
      href: '/games',
      icon: Gamepad2,
      current: location.pathname === '/games'
    },
    {
      name: 'Leaderboard',
      href: '/leaderboard',
      icon: Trophy,
      current: location.pathname === '/leaderboard'
    },
    {
      name: 'Profile',
      href: '/profile',
      icon: User,
      current: location.pathname === '/profile'
    },
    {
      name: 'Subscription',
      href: '/subscription',
      icon: Crown,
      current: location.pathname === '/subscription'
    },
    {
      name: 'Server Management',
      href: '/servers',
      icon: Server,
      current: location.pathname === '/servers'
    }
  ];

  // Add analytics and admin for server subscription users
  if (user?.subscription?.type === 'server') {
    navigation.push({
      name: 'Analytics',
      href: '/analytics',
      icon: BarChart3,
      current: location.pathname === '/analytics'
    });
    
    navigation.push({
      name: 'Admin Panel',
      href: '/admin',
      icon: Settings,
      current: location.pathname === '/admin'
    });
  }

  return (
    <div className="fixed inset-y-0 left-0 z-50 w-64 bg-discord-darker border-r border-discord-dark">
      <div className="flex flex-col h-full">
        {/* User Info */}
        <div className="p-6 border-b border-discord-dark">
          <div className="flex items-center space-x-3">
            {user?.avatar ? (
              <img
                src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
                alt={user.username}
                className="h-12 w-12 rounded-full"
              />
            ) : (
              <div className="h-12 w-12 rounded-full bg-discord-blurple flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-discord-lightest truncate">
                {user?.username}
              </p>
              <p className="text-xs text-discord-light truncate">
                #{user?.discriminator}
              </p>
            </div>
          </div>
          
          <div className="mt-3">
            {user?.subscription?.type === 'free' ? (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-discord-light">Free Plan</span>
                <Link
                  to="/subscription"
                  className="text-xs text-discord-blurple hover:text-discord-blurple/80 transition-colors"
                >
                  Upgrade
                </Link>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Crown className="h-4 w-4 text-discord-yellow" />
                <span className="text-xs text-discord-yellow capitalize">
                  {user?.subscription?.type} Plan
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  item.current
                    ? 'bg-discord-blurple text-white'
                    : 'text-discord-light hover:text-discord-lightest hover:bg-discord-dark'
                }`}
              >
                <Icon
                  className={`mr-3 h-5 w-5 ${
                    item.current ? 'text-white' : 'text-discord-light group-hover:text-discord-lightest'
                  }`}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Quick Stats */}
        <div className="p-4 border-t border-discord-dark">
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-discord-light">
              <span>Games Today</span>
              <span>{user?.gameStats?.gamesToday || 0}/10</span>
            </div>
            
            {user?.subscription?.type === 'free' && (
              <div className="w-full bg-discord-dark rounded-full h-1.5">
                <div
                  className="bg-discord-blurple h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min((user?.gameStats?.gamesToday || 0) * 10, 100)}%`
                  }}
                ></div>
              </div>
            )}
            
            <div className="flex justify-between text-xs text-discord-light">
              <span>Total Games</span>
              <span>{user?.gameStats?.totalGamesPlayed || 0}</span>
            </div>
            
            <div className="flex justify-between text-xs text-discord-light">
              <span>Win Rate</span>
              <span>{user?.gameStats?.winRate || 0}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
